/* eslint-disable @typescript-eslint/no-explicit-any */
import { AggLineChartEntity, AggOneDimDataItem } from '@patsnap/synapse_domain'
import { defineComponent, onMounted, PropType, shallowRef, toRefs, unref, watch } from '@vue/composition-api'
import { EChartsOption } from 'echarts'
import { cloneDeep } from 'lodash'
import { useAnalysisChartTooltip } from '../../compositions/useAnalysisChartTooltip'
import { useAnalysisEchartsHelpers } from '../../compositions/useAnalysisEchartsHelpers'
import { BAnalysisEChart } from './BAnalysisEchart'

type AnalysisLineEchartTooltipData = {
  seriesName: string
  categoryName: string
  count: number
  color: string
}

export const BAnalysisLineEchart = defineComponent({
  name: 'BAnalysisLineEchart',
  props: {
    entity: {
      type: Object as PropType<AggLineChartEntity>,
      required: true,
    },
  },
  setup(props, { emit }) {
    const { entity } = toRefs(props)

    const { tooltipRef, tooltipCommonConfig, tooltipActiveData, tooltipFormatter, resetTooltipDataMap } =
      useAnalysisChartTooltip<AnalysisLineEchartTooltipData>({
        pickTooltipDataFromChartParam,
      })

    const realOption = shallowRef<EChartsOption>()

    function updateRealOption() {
      const option = cloneDeep(unref(entity).getEchartsOptions())

      if (option) {
        option.tooltip = {
          show: true,
          trigger: 'axis',
          axisPointer: {
            type: 'line',
            z: -1,
          },
          enterable: false,
          hideDelay: 500,
          triggerOn: 'mousemove|click',
          formatter: tooltipFormatter,
          ...tooltipCommonConfig,
        }

        option.animation = false
      }

      realOption.value = option
    }

    function pickTooltipDataFromChartParam(params: any) {
      if (!Array.isArray(params)) return
      const firstParam = params[0]
      if (!firstParam) return
      const seriesName = firstParam.seriesName
      if (!seriesName) return
      const value = firstParam.data as AggOneDimDataItem
      const info: AnalysisLineEchartTooltipData = {
        seriesName: seriesName,
        categoryName: value.name,
        count: value.count,
        color: firstParam.color,
      }
      return [value.name, info] as [string, AnalysisLineEchartTooltipData]
    }

    function handleClickItem(params: any) {
      if (!params) return
      if (!params.data) return
      emit('clickItem', params.data as AggOneDimDataItem)
    }

    onMounted(() => {
      updateRealOption()
    })

    watch(entity, () => {
      resetTooltipDataMap()
      updateRealOption()
    })

    return {
      tooltipRef,
      realOption,
      tooltipActiveData,
      handleClickItem,
      ...useAnalysisEchartsHelpers(),
    }
  },
  render() {
    return (
      <BAnalysisEChart
        ref="analysisChartRef"
        displayType="single-line"
        option={this.realOption}
        axisPopperOption={{ xPopperEnable: false, yPopperEnable: false }}
        {...{
          on: {
            click: this.handleClickItem,
          },
        }}
      >
        <template slot="extra">
          <div ref="tooltipRef">
            <div class="p-2">
              <div class="flex items-center mb-2">
                <span
                  class="flex-shrink-0 inline-block h-[10px] w-[10px] rounded-full mr-2"
                  style={{ backgroundColor: this.tooltipActiveData?.color }}
                ></span>
                <span class="text-xs leading-4 font-bold text-text-t1">{this.tooltipActiveData?.categoryName}</span>
              </div>
              <div class="pl-[18px] mt-2">
                <div class="text-xs leading-4">
                  <span class="text-text-t1">{this.tooltipActiveData?.seriesName}: </span>
                  <span class="text-text-t2">{this.tooltipActiveData?.count}</span>
                </div>
              </div>
            </div>
          </div>
        </template>
      </BAnalysisEChart>
    )
  },
})
